// apps/web/src/pages/spread/constraint-pill.tsx
//
// Phase 11.7 Plan 01 Task 3 — shared constraint-pill primitives.
//
// LIFTED VERBATIM from RecipeOptionGroup.tsx so Surface 1 (Plan 02, the
// interactive blocked-option branch) and Surface 2 (Plan 03, the per-batch
// Duplicate pill) import ONE implementation instead of reaching into Plan 02's
// same-wave edit of RecipeOptionGroup.tsx. This is the Wave-1 import seam:
// both downstream plans (same Wave 2) depend on these exports; neither
// re-defines them, and Plan 03 no longer touches RecipeOptionGroup.tsx at all.
//
// Pure relocation — same JSX, same props, same aria/data attributes, same
// className. No behavior change: RecipeOptionGroup.test.tsx stays green after
// the import-swap. Two copies of this tooltip would re-introduce the exact
// drift Pitfall 1 warns about.

import type { ConstraintVerdict } from "@the-recipe-spread/shared";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ---------------------------------------------------------------------------
// firstViolatedFor — given a widget instanceId, find the first violated
// verdict whose constraint plausibly targets this widget (v1 simplest-correct
// heuristic: any violated verdict surfaces; refined per-option targeting is
// Phase 13+ work once selectability constraints expand to carry option ids).
// ---------------------------------------------------------------------------

export function firstViolatedFor(
  _instanceId: string,
  verdicts: ReadonlyArray<ConstraintVerdict>,
): ConstraintVerdict | undefined {
  for (const v of verdicts) {
    if (v.status === "violated") return v;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// DisabledOptionWithTooltip — pill with aria-disabled="true" wrapped in a
// Radix Tooltip whose content is the kind label + substituted explanation.
// Mission Critical: aria-disabled (NOT disabled) preserves focusability per
// UI-SPEC line 401, so screen-reader users land on it and hear the tooltip
// via aria-describedby (Radix manages this binding automatically).
//
// SECURITY (T-09-13-03 / T-11.7-15 mitigation): the substituted explanation
// flows through React JSX text-escaping as a plain text child — NEVER through
// dangerouslySetInnerHTML. substituteExplanation is a pure string-replace; the
// caller (OptionSetSurface) performs the substitution and passes the result as
// the `explanation` string prop.
// ---------------------------------------------------------------------------

interface DisabledOptionWithTooltipProps {
  optionId: string;
  label: string;
  kindLabel: string;
  explanation: string;
}

export function DisabledOptionWithTooltip({
  optionId,
  label,
  kindLabel,
  explanation,
}: DisabledOptionWithTooltipProps): React.ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-disabled="true"
          data-option-id={optionId}
          data-constraint-disabled
          className="inline-flex items-center rounded-full border border-[var(--recipe-line)] bg-[var(--recipe-card)] px-3 py-1 text-sm text-[var(--recipe-ink)] opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent
        className="recipe-tooltip max-w-xs border-[var(--recipe-line)] bg-[var(--recipe-card)] text-[var(--recipe-ink)]"
      >
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--recipe-accent)]">
            {kindLabel}
          </span>
          <span className="text-sm leading-snug">{explanation}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
